import { ValidationError } from "src/models/validation-error";
import { UserType } from "src/common/enums";

export interface ConfirmAddUserByAliasEntry {
    principalArn: string,
    userType: UserType,
    rowNumber: number,
    email?: string,
}

// Fields mirror the CONFIRM_BULK_ADD_USER_BY_ALIAS_MUTATION selection set.
export interface AddByAliasValidationResult {
    rowNumber: number,
    alias: string,
    userType: UserType,
    errors: ValidationError[],
}

// Confirm mutation types

interface ConfirmBulkAddUserByAliasInput {
    scope: string,
    tenantId: string,
    users: ConfirmAddUserByAliasEntry[],
}

interface BulkAddUserByAliasConfirmResponse {
    validationResults: AddByAliasValidationResult[],
    commonValidationErrors: ValidationError[],
    successCount: number,
}

export interface ConfirmBulkAddUserByAliasData {
    confirmBulkAddUserByAlias: BulkAddUserByAliasConfirmResponse,
}

export interface ConfirmBulkAddUserByAliasVariables {
    confirmBulkAddUserByAliasInput: ConfirmBulkAddUserByAliasInput,
}

// --- Bulk add page helpers (these shape the data sent to the GraphQL/PCS API) ---

// The User Admin roles for which the backend (PCS, via the GraphQL endpoint)
// REQUIRES an email; we must send it for these roles or PCS rejects the row.
// Mirrors the single modal's requiresEmail rule (add-user-modal.tsx).
const requiresEmail = (userType: UserType): boolean =>
    userType === UserType.USER_ADMIN_PRIMARY || userType === UserType.USER_ADMIN_SECONDARY;

// One parsed Excel data row. rowNumber is the original Excel row and never
// changes. `alias` is the user's system access credential (shown to users as
// "System Access Credential"); it is resolved to a principal by getUserInfoByAlias.
export interface ParsedAliasRow {
    rowNumber: number,
    alias: string,
    userType: UserType,
    email: string,
}

// A person returned by getUserInfoByAlias.
export interface ResolvedPerson {
    alias: string,
    principalArn: string,
    firstName: string,
    lastName: string,
    jobType: string,
    isActive: boolean,
}

export interface ResolvedRow {
    rowNumber: number,
    person: ResolvedPerson,
    userType: UserType,
    email: string,
}

export interface FlaggedRow {
    rowNumber: number,
    alias: string,
    reasonKey: string,
}

export interface ClassifiedRows {
    resolved: ResolvedRow[],
    flagged: FlaggedRow[],
}

const normalize = (value: string): string => value.trim().toLowerCase();

// Joins parsed rows to resolved persons by alias (trim + case-insensitive).
// Unmatched -> unresolved; matched-but-inactive -> inactive. Both are flagged and
// excluded from the confirm call. Order and rowNumber are preserved.
export const classifyRows = (rows: ParsedAliasRow[], persons: ResolvedPerson[]): ClassifiedRows => {
    const byAlias = new Map<string, ResolvedPerson>();
    persons.forEach(p => byAlias.set(normalize(p.alias), p));

    const resolved: ResolvedRow[] = [];
    const flagged: FlaggedRow[] = [];

    rows.forEach(row => {
        const person = byAlias.get(normalize(row.alias));
        if (!person) {
            flagged.push({ rowNumber: row.rowNumber, alias: row.alias, reasonKey: "bulk-add-user-row-unresolved" });
        } else if (!person.isActive) {
            flagged.push({ rowNumber: row.rowNumber, alias: row.alias, reasonKey: "bulk-add-user-row-inactive" });
        } else {
            resolved.push({ rowNumber: row.rowNumber, person, userType: row.userType, email: row.email });
        }
    });

    return { resolved, flagged };
};

// Builds confirm entries from resolved rows, preserving rowNumber. Email is
// attached only for User Admin roles; PCS is the sole authority that validates it.
export const buildConfirmEntries = (rows: ResolvedRow[]): ConfirmAddUserByAliasEntry[] =>
    rows.map(row => {
        const entry: ConfirmAddUserByAliasEntry = {
            principalArn: row.person.principalArn,
            userType: row.userType,
            rowNumber: row.rowNumber,
        };
        if (requiresEmail(row.userType)) {
            entry.email = (row.email ?? "").trim();
        }
        return entry;
    });
